import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek } from "@/lib/format";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function Budgets() {
  const { settings } = useAuth();
  const cur = settings.currency || "£";
  const [week, setWeek] = useState(currentWeek());
  const [sites, setSites] = useState([]);
  const [budgets, setBudgets] = useState(null);
  const [adjDlg, setAdjDlg] = useState(null);
  const [adj, setAdj] = useState({ amount: "", reason: "" });
  const [allocForm, setAllocForm] = useState({ site_id: "", allocation: "" });

  const load = () => { setBudgets(null); api.get(`/budgets?week=${week}`).then((r) => setBudgets(r.data)); };
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data)); }, []);
  useEffect(() => { load(); }, [week]);

  const allocate = async () => {
    if (!allocForm.site_id || !allocForm.allocation) { toast.error("Select a home and amount"); return; }
    try {
      await api.post("/budgets", { site_id: allocForm.site_id, week_start: week, allocation: parseFloat(allocForm.allocation) });
      toast.success("Budget allocated"); setAllocForm({ site_id: "", allocation: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const saveAdj = async () => {
    try {
      await api.post(`/budgets/${adjDlg.id}/adjust`, { amount: parseFloat(adj.amount), reason: adj.reason });
      toast.success("Adjustment recorded"); setAdjDlg(null); setAdj({ amount: "", reason: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || id;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Weekly Budgets</h1>
          <p className="text-slate-500 text-sm mt-1">Every week is a separate financial period. Adjustments are audited.</p>
        </div>
        <WeekSelect value={week} onChange={setWeek} />
      </div>

      <Card className="border-border p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Home</Label>
            <Select value={allocForm.site_id} onValueChange={(v) => setAllocForm({ ...allocForm, site_id: v })}>
              <SelectTrigger data-testid="alloc-site" className="w-[200px] mt-1"><SelectValue placeholder="Select home" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Allocation ({cur})</Label><Input data-testid="alloc-amount" type="number" value={allocForm.allocation} onChange={(e) => setAllocForm({ ...allocForm, allocation: e.target.value })} className="w-[160px] mt-1" placeholder="500" /></div>
          <Button data-testid="alloc-submit" onClick={allocate} className="bg-[#0F172A] hover:bg-[#1E293B]">Allocate for this week</Button>
        </div>
      </Card>

      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Home</TableHead>
                <TableHead className="text-right">Allocation</TableHead>
                <TableHead className="text-right">Approved Spend</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Adjustments</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {budgets === null && <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
              {budgets && budgets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No budgets allocated for this week yet.</TableCell></TableRow>}
              {budgets && budgets.map((b) => (
                <TableRow key={b.id} data-testid={`budget-row-${b.id}`}>
                  <TableCell className="font-medium text-slate-800">{siteName(b.site_id)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{money(b.allocation, cur)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{money(b.spent, cur)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-medium">{money(b.remaining, cur)}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "reconciled" ? "bg-slate-200 text-slate-600" : "bg-emerald-50 text-emerald-700"}`}>{b.status}</span></TableCell>
                  <TableCell className="text-slate-500 text-sm">{b.adjustments?.length || 0}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`adjust-${b.id}`} disabled={b.status === "reconciled"} onClick={() => setAdjDlg(b)}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Adjust</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!adjDlg} onOpenChange={(o) => !o && setAdjDlg(null)}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-head">Budget adjustment</DialogTitle></DialogHeader>
          {adjDlg && <p className="text-sm text-slate-500">{siteName(adjDlg.site_id)} · current {money(adjDlg.allocation, cur)}</p>}
          <div><Label>Amount (use negative to reduce)</Label><Input data-testid="adj-amount" type="number" step="0.01" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="mt-1.5" placeholder="e.g. 50 or -25" /></div>
          <div><Label>Reason</Label><Input data-testid="adj-reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="mt-1.5" placeholder="Reason for adjustment" /></div>
          <DialogFooter><Button data-testid="save-adjust" onClick={saveAdj} className="bg-[#0F172A] hover:bg-[#1E293B]">Record adjustment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
